import type { CSSProperties, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';
import type { TFunction } from 'i18next';

import { LATON } from '#components/mobile/ayuda/OjoBuey';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { color, movimiento, texto } from '#components/mobile/ui/tokens';

import { TAMANOS_PERMITIDOS } from './modeloWidgets';
import type { IdWidget, TamanoVisible } from './modeloWidgets';
import { descripcionWidget, nombreWidget } from './widgets/registro';

/** Icono de cada tarjeta en el catálogo «Añadir tarjeta». */
export const ICONO_WIDGET: Record<IdWidget, NombreIcono> = {
  hoy: 'wallet',
  'por-hacer': 'check',
  fijadas: 'target',
  'cuenta-comun': 'users',
  consejos: 'bulb',
  'resumen-mes': 'pie',
  deudas: 'bank',
  'gasto-mes': 'receipt',
  huchas: 'piggy',
  'meses-cubiertos': 'cal',
  'pagas-extra': 'coins',
  'a-bordo': 'velero',
  'pagos-fijos': 'calclock',
  'informe-ritmo': 'velero',
  'informe-se-va': 'pie',
  'informe-se-pasan': 'alert',
  'informe-ahorro': 'piggy',
  'informe-patrimonio': 'anchor',
  'informe-semana': 'cal',
  'informe-categoria': 'tag',
  'informe-ingresos': 'coins',
  'informe-margen': 'clock',
  'informe-diario': 'note',
};

export function nombreTamanoVisible(t: TFunction, tamano: TamanoVisible) {
  switch (tamano) {
    case 'pequena':
      return t('Small');
    case 'grande':
      return t('Large');
    default:
      return t('Wide');
  }
}

const sube = keyframes({
  from: { opacity: 0, transform: 'translateY(16px)' },
  to: { opacity: 1, transform: 'none' },
});

/** Panel flotante abajo (barra de acciones y selector de tamaño). */
const clasePanel = css({
  position: 'fixed',
  left: 10,
  right: 10,
  bottom: 'calc(16px + env(safe-area-inset-bottom))',
  zIndex: 150,
  backgroundColor: color.surface,
  borderRadius: 18,
  boxShadow: `0 10px 30px rgba(14, 20, 36, 0.28), 0 0 0 1.5px ${LATON.oscuro}`,
  padding: 10,
  animation: `${sube} 250ms ${movimiento.muelle} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const estiloOpcion: CSSProperties = {
  flex: 1,
  minHeight: 48,
  borderRadius: 12,
  backgroundColor: color.surface2,
  fontSize: 13,
  fontWeight: 800,
  color: color.fg,
};

/**
 * Elegir el tamaño: solo los que esa tarjeta admite, cada uno con su
 * dibujo (media fila, fila entera o fila entera y más alta).
 */
export function SelectorTamano({
  id,
  actual,
  onElegir,
  onCerrar,
}: {
  id: IdWidget;
  actual: TamanoVisible;
  onElegir: (tamano: TamanoVisible) => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={clasePanel}
      role="dialog"
      aria-label={t('Size of {{name}}', { name: nombreWidget(t, id) })}
      data-testid="inicio-selector-tamano"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '0 4px 8px',
        }}
      >
        <Text style={{ fontSize: 13, fontWeight: 800, color: color.fg3 }}>
          {t('Size of {{name}}', { name: nombreWidget(t, id) })}
        </Text>
        <Button
          variant="bare"
          aria-label={t('Close')}
          data-testid="inicio-selector-cerrar"
          onPress={onCerrar}
          style={{ width: 44, height: 32, color: color.fg3 }}
        >
          <Icono nombre="x" size={18} />
        </Button>
      </View>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {TAMANOS_PERMITIDOS[id].map(tam => (
          <Button
            key={tam}
            variant="bare"
            aria-pressed={actual === tam}
            onPress={() => onElegir(tam)}
            style={{
              ...estiloOpcion,
              flexDirection: 'column',
              gap: 6,
              padding: '10px 6px 8px',
              ...(actual === tam && {
                backgroundColor: `${LATON.claro}73`,
                boxShadow: `inset 0 0 0 2px ${LATON.oscuro}`,
              }),
            }}
          >
            <View
              aria-hidden
              style={{
                width: 52,
                height: tam === 'grande' ? 44 : 30,
                marginTop: tam === 'grande' ? -7 : 0,
                borderRadius: 5,
                backgroundColor: color.surface3,
                padding: 3,
              }}
            >
              <View
                style={{
                  width: tam === 'pequena' ? 20 : 46,
                  flex: 1,
                  borderRadius: 3,
                  backgroundColor: color.accent,
                }}
              />
            </View>
            {nombreTamanoVisible(t, tam)}
          </Button>
        ))}
      </View>
    </div>
  );
}

/**
 * Sin arrastrar (o con lector de pantalla): tocar una tarjeta al editar la
 * elige y salen Subir, Bajar, Tamaño y Quitar.
 */
export function BarraElegida({
  id,
  puedeSubir,
  puedeBajar,
  onSubir,
  onBajar,
  onTamano,
  onQuitar,
}: {
  id: IdWidget;
  puedeSubir: boolean;
  puedeBajar: boolean;
  onSubir: () => void;
  onBajar: () => void;
  onTamano: () => void;
  onQuitar: () => void;
}) {
  const { t } = useTranslation();
  const nombre = nombreWidget(t, id);
  const boton = (
    etiqueta: string,
    contenido: ReactNode,
    onPress: () => void,
    activo = true,
  ) => (
    <Button
      variant="bare"
      aria-label={etiqueta}
      isDisabled={!activo}
      onPress={onPress}
      style={{ ...estiloOpcion, opacity: activo ? 1 : 0.4 }}
    >
      {contenido}
    </Button>
  );
  return (
    <div
      className={clasePanel}
      role="toolbar"
      aria-label={nombre}
      data-testid="inicio-barra-elegida"
      style={{ display: 'flex', gap: 6, padding: 8 }}
    >
      {boton(
        t('Move {{name}} up', { name: nombre }),
        <Trans>Up (move)</Trans>,
        onSubir,
        puedeSubir,
      )}
      {boton(
        t('Move {{name}} down', { name: nombre }),
        <Trans>Down (move)</Trans>,
        onBajar,
        puedeBajar,
      )}
      {boton(
        t('Size of {{name}}', { name: nombre }),
        <Trans>Size</Trans>,
        onTamano,
        TAMANOS_PERMITIDOS[id].length > 1,
      )}
      {boton(
        t('Remove {{name}}', { name: nombre }),
        <Trans>Remove</Trans>,
        onQuitar,
      )}
    </div>
  );
}

/** «+ Añadir tarjeta» al final de la rejilla, al editar. */
export function BotonAnadirTarjeta({ onPress }: { onPress: () => void }) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      data-testid="inicio-anadir-tarjeta"
      style={({ isPressed }) => ({
        minHeight: 54,
        borderRadius: 16,
        border: `2px dashed ${LATON.oscuro}`,
        backgroundColor: `${LATON.claro}2e`,
        color: color.fg,
        fontSize: 15,
        fontWeight: 800,
        transform: isPressed ? 'scale(0.98)' : undefined,
        transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
      })}
    >
      <Trans>+ Add card</Trans>
    </Button>
  );
}

/**
 * Catálogo «Añadir tarjeta»: las que no están en Inicio, con su explicación
 * (la misma que da Gavi) y los tamaños que admite. Al pie, volver al Inicio
 * de siempre.
 */
export function CatalogoInicio({
  disponibles,
  pantalla = 'inicio',
  onAnadir,
  onRestablecer,
  onCerrar,
}: {
  disponibles: readonly IdWidget[];
  /** De qué pantalla es el catálogo (cambian los textos del pie). */
  pantalla?: 'inicio' | 'informes';
  onAnadir: (id: IdWidget) => void;
  onRestablecer: () => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Hoja
      aria-label={t('Add card')}
      data-testid="inicio-catalogo"
      onCerrar={onCerrar}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '0 0 2px 4px',
        }}
      >
        <Text style={{ ...texto.titulo, fontSize: 19 }}>
          <Trans>Add card</Trans>
        </Text>
        <Button
          variant="bare"
          aria-label={t('Close')}
          data-testid="catalogo-cerrar"
          onPress={onCerrar}
          style={{ width: 44, height: 44, color: color.fg3 }}
        >
          <Icono nombre="x" size={20} />
        </Button>
      </View>
      <Text
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: color.fg3,
          margin: '0 4px 12px',
        }}
      >
        <Trans>Each card, with what it shows and the sizes it comes in.</Trans>
      </Text>
      {disponibles.length === 0 && (
        <Text style={{ ...texto.cuerpo, color: color.fg2, margin: '8px 4px' }}>
          {pantalla === 'informes' ? (
            <Trans>All the cards are already in your reports.</Trans>
          ) : (
            <Trans>All the cards are already on your home.</Trans>
          )}
        </Text>
      )}
      <View style={{ gap: 10 }}>
        {disponibles.map(id => (
          <View
            key={id}
            data-testid={`catalogo-${id}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: 10,
              borderRadius: 16,
              backgroundColor: color.surface2,
            }}
          >
            <IconoCaja icono={ICONO_WIDGET[id]} tono="neutro" size={44} />
            <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <Text style={{ fontSize: 15, fontWeight: 800, color: color.fg }}>
                {nombreWidget(t, id)}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  lineHeight: 1.35,
                  fontWeight: 600,
                  color: color.fg2,
                }}
              >
                {descripcionWidget(t, id)}
              </Text>
              <Text style={{ fontSize: 12, fontWeight: 700, color: color.fg3 }}>
                {TAMANOS_PERMITIDOS[id]
                  .map(tam => nombreTamanoVisible(t, tam))
                  .join(' · ')}
              </Text>
            </View>
            <Boton
              onPress={() => onAnadir(id)}
              aria-label={t('Add {{name}}', { name: nombreWidget(t, id) })}
              style={{ padding: '0 14px' }}
            >
              <Trans>Add (card)</Trans>
            </Boton>
          </View>
        ))}
      </View>
      <Boton
        variante="fantasma"
        onPress={onRestablecer}
        style={{ alignSelf: 'center', marginTop: 16 }}
      >
        <Icono nombre="refresh" size={18} />
        {pantalla === 'informes' ? (
          <Trans>Back to the usual reports</Trans>
        ) : (
          <Trans>Back to the usual home</Trans>
        )}
      </Boton>
    </Hoja>
  );
}
